import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { auth } from '../firebase';
import { schedulerApiFetch } from '../api/schedulerClient';
import { userConfigKeys } from './assignmentChunkKeys';

const USER_CONFIG_STALE_MS = 5 * 60 * 1000;

async function fetchUserConfigQuery() {
  const user = auth.currentUser;
  if (!user) return null;

  const token = await user.getIdToken();
  if (!token) return null;

  const res = await schedulerApiFetch('user-config', {
    bearerToken: token,
  });

  if (!res.ok) return null;

  const data = await res.json();
  if (!data.success) return null;

  return {
    config: data.config || null,
    isDefault: Boolean(data.is_default),
  };
}

export function useUserConfig(options = {}) {
  const uid = auth.currentUser?.uid;
  const enabled = (options.enabled ?? true) && Boolean(uid);

  return useQuery({
    queryKey: userConfigKeys.detail(uid),
    queryFn: fetchUserConfigQuery,
    enabled,
    staleTime: USER_CONFIG_STALE_MS,
    gcTime: 30 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function getTimezoneFromUserConfig(config) {
  if (!config) return null;
  return config.la_timezone || config.timeZone || null;
}

export function getCompanyNameFromUserConfig(config) {
  if (!config) return null;
  return config.company_name || null;
}

export function useInvalidateUserConfig() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: userConfigKeys.all });
  }, [queryClient]);
}
